import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Map as MapGL, Marker } from "react-map-gl/mapbox";
import { MapViewState, WebMercatorViewport } from "@deck.gl/core";
import { GeoJsonLayer } from "@deck.gl/layers";
import type { Feature, Polygon } from "geojson";

import useMapViewState from "@/features/shared/widgets/map/hooks/useMapViewState";
import {
  accountOwnedMapStyleUrl,
  knownMapboxStyles,
  resolveBaseLayerToMapStyleUrl,
} from "@/lib/mapbox/base-layer-styles";
import type { ParametersGeoBoundingBox } from "@/lib/api";
import { DeckGLOverlay } from "@/components/deckgl/deckgl-overlay";

import "@/features/shared/widgets/map/map.css";

import styles from "./bounds-preview-map.module.css";

type DragHandle = "north" | "south" | "west" | "east" | "center";

interface DragState {
  handle: DragHandle;
  startClientX: number;
  startClientY: number;
  startBounds: ParametersGeoBoundingBox;
}

function boundsToInitialCoords(bounds: ParametersGeoBoundingBox) {
  return [
    { lat: bounds.south_latitude, lng: bounds.west_longitude },
    { lat: bounds.north_latitude, lng: bounds.east_longitude },
  ];
}

function boundsToPolygonFeature(bounds: ParametersGeoBoundingBox): Feature<Polygon> {
  const { west_longitude, south_latitude, east_longitude, north_latitude } = bounds;
  return {
    type: "Feature",
    properties: {},
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [west_longitude, south_latitude],
          [east_longitude, south_latitude],
          [east_longitude, north_latitude],
          [west_longitude, north_latitude],
          [west_longitude, south_latitude],
        ],
      ],
    },
  };
}

export type BoundsPreviewInteractionMode = "none" | "readOnly" | "editable";

export interface BoundsPreviewMapProps {
  bounds: ParametersGeoBoundingBox;
  showBoundsOverlay?: boolean;
  fitBoundsPadding?: number;
  /** Model map `base_layer_style` API value; resolves to account Mapbox style URL. */
  baseLayerStyle?: string;
  /** When provided, renders drag handles so the user can resize/move the bounds. */
  onBoundsChange?: (bounds: ParametersGeoBoundingBox) => void;
  /**
   * When set, drives overlay + drag handles without inferring from legacy props.
   * Prefer passing from smart composers (`editable` on viewport tab, `readOnly` elsewhere).
   * When omitted, legacy behaviour: overlay from `showBoundsOverlay`, handles when `onBoundsChange` is set.
   */
  boundsInteractionMode?: BoundsPreviewInteractionMode;
}

export function BoundsPreviewMap({
  bounds,
  showBoundsOverlay = false,
  fitBoundsPadding = 40,
  baseLayerStyle,
  onBoundsChange,
  boundsInteractionMode,
}: BoundsPreviewMapProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  // Mutable refs for values used in drag event handlers — avoids stale closures and
  // prevents the window listeners from being re-registered on every bounds update.
  const boundsRef = useRef(bounds);
  boundsRef.current = bounds;
  const onBoundsChangeRef = useRef(onBoundsChange);
  onBoundsChangeRef.current = onBoundsChange;
  const dragRef = useRef<DragState | null>(null);
  const viewStateRef = useRef<MapViewState | undefined>(undefined);

  const showBoundsLayer =
    boundsInteractionMode === "readOnly" || boundsInteractionMode === "editable"
      ? true
      : boundsInteractionMode === "none"
        ? false
        : showBoundsOverlay;

  const showDragHandles =
    boundsInteractionMode === "editable"
      ? Boolean(onBoundsChange)
      : boundsInteractionMode === "readOnly" || boundsInteractionMode === "none"
        ? false
        : Boolean(onBoundsChange);

  // While dragging, pin the map view to the bounds at drag-start so the viewport
  // stays still. On mouseup the pin is released and the view re-fits to the result.
  const [isDragging, setIsDragging] = useState(false);
  const dragStartBoundsRef = useRef<ParametersGeoBoundingBox>(bounds);

  const mapStyleUrl = useMemo(
    () =>
      baseLayerStyle
        ? resolveBaseLayerToMapStyleUrl(baseLayerStyle)
        : accountOwnedMapStyleUrl(knownMapboxStyles.MonochromeLight),
    [baseLayerStyle],
  );

  const boundsForView = isDragging ? dragStartBoundsRef.current : bounds;
  const initialCoords = useMemo(
    () => boundsToInitialCoords(boundsForView),
    [boundsForView],
  );

  const fitBoundsOptions = useMemo(
    () => ({
      padding: fitBoundsPadding,
    }),
    [fitBoundsPadding],
  );

  const { mapViewState } = useMapViewState({
    containerRef: mapContainerRef,
    initialCoords,
    fitBoundsOptions,
  });

  useEffect(() => {
    viewStateRef.current = mapViewState;
  }, [mapViewState]);

  const boundsLayer = useMemo(() => {
    if (!showBoundsLayer) return null;
    const feature = boundsToPolygonFeature(bounds);
    return new GeoJsonLayer({
      id: "bounds-overlay",
      data: feature,
      getFillColor: [59, 130, 246, 30],
      getLineColor: [59, 130, 246],
      lineWidthMinPixels: 2,
      filled: true,
      stroked: true,
      opacity: 0.6,
    });
  }, [bounds, showBoundsLayer]);

  const layers = useMemo(() => (boundsLayer ? [boundsLayer] : []), [boundsLayer]);

  const unprojectPoint = useCallback((clientX: number, clientY: number) => {
    const container = mapContainerRef.current;
    const vs = viewStateRef.current as
      | { longitude: number; latitude: number; zoom: number }
      | undefined;
    if (!container || !vs) return null;
    const rect = container.getBoundingClientRect();
    const viewport = new WebMercatorViewport({
      width: container.clientWidth,
      height: container.clientHeight,
      longitude: vs.longitude,
      latitude: vs.latitude,
      zoom: vs.zoom,
    });
    const [lng, lat] = viewport.unproject([clientX - rect.left, clientY - rect.top]);
    return { lng, lat };
  }, []);

  const handleMouseDown = useCallback(
    (handle: DragHandle) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragStartBoundsRef.current = boundsRef.current;
      dragRef.current = {
        handle,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startBounds: boundsRef.current,
      };
      setIsDragging(true);
    },
    [],
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      const onChange = onBoundsChangeRef.current;
      if (!drag || !onChange) return;

      const current = unprojectPoint(e.clientX, e.clientY);
      if (!current) return;

      const { handle, startClientX, startClientY, startBounds } = drag;
      const cb = boundsRef.current;

      if (handle === "center") {
        const start = unprojectPoint(startClientX, startClientY);
        if (!start) return;
        const dLat = current.lat - start.lat;
        const dLng = current.lng - start.lng;
        onChange({
          ...startBounds,
          north_latitude: startBounds.north_latitude + dLat,
          south_latitude: startBounds.south_latitude + dLat,
          east_longitude: startBounds.east_longitude + dLng,
          west_longitude: startBounds.west_longitude + dLng,
        });
      } else if (handle === "north") {
        onChange({ ...cb, north_latitude: Math.max(cb.south_latitude + 0.1, current.lat) });
      } else if (handle === "south") {
        onChange({ ...cb, south_latitude: Math.min(cb.north_latitude - 0.1, current.lat) });
      } else if (handle === "east") {
        onChange({ ...cb, east_longitude: Math.max(cb.west_longitude + 0.1, current.lng) });
      } else if (handle === "west") {
        onChange({ ...cb, west_longitude: Math.min(cb.east_longitude - 0.1, current.lng) });
      }
    };

    const handleMouseUp = () => {
      dragRef.current = null;
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [unprojectPoint]);

  const midLat = (bounds.north_latitude + bounds.south_latitude) / 2;
  const midLng = (bounds.west_longitude + bounds.east_longitude) / 2;

  const mapTileAccessToken = import.meta.env.VITE_MAP_ACCESS_TOKEN;

  return (
    <div ref={mapContainerRef} className={styles.boundsPreviewMapContainer}>
      {mapViewState && (
        <MapGL
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error Deck.gl MapViewState does not match react-map-gl ViewState perfectly
          viewState={mapViewState}
          attributionControl={false}
          maxPitch={0}
          bearing={0}
          bearingSnap={360}
          mapboxAccessToken={mapTileAccessToken}
          mapStyle={mapStyleUrl}
          projection={{ name: "mercator" }}
          interactive={false}
        >
          <DeckGLOverlay
            interleaved
            layers={layers}
            // Disables cursor:grab, needed since the map is not interactive.
            getCursor={() => "default"}
          />
          {showDragHandles && onBoundsChange && (
            <>
              <Marker longitude={midLng} latitude={bounds.north_latitude} anchor="center">
                <div
                  className={styles.handleNorth}
                  onMouseDown={handleMouseDown("north")}
                />
              </Marker>
              <Marker longitude={bounds.west_longitude} latitude={midLat} anchor="center">
                <div className={styles.handleWest} onMouseDown={handleMouseDown("west")} />
              </Marker>
              <Marker longitude={midLng} latitude={midLat} anchor="center">
                <div
                  className={styles.handleCenter}
                  onMouseDown={handleMouseDown("center")}
                />
              </Marker>
              <Marker longitude={bounds.east_longitude} latitude={midLat} anchor="center">
                <div className={styles.handleEast} onMouseDown={handleMouseDown("east")} />
              </Marker>
              <Marker longitude={midLng} latitude={bounds.south_latitude} anchor="center">
                <div
                  className={styles.handleSouth}
                  onMouseDown={handleMouseDown("south")}
                />
              </Marker>
            </>
          )}
        </MapGL>
      )}
    </div>
  );
}
